import { Phone, Mail, Facebook, Lock } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE } from "../data/site";

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  const goToAnchor = (id) => {
    if (onHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 60);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#030303] border-t border-[#1a1a1a]"
    >
      <div className="container-x px-6 md:px-12 lg:px-24 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img
              src={SITE.logo}
              alt="Swift Luxury Walls"
              className="h-12 w-12 rounded-md"
            />
            <div>
              <div className="font-display text-xl text-white">Swift Luxury Walls</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">
                Premier Custom Entertainment Creations
              </div>
            </div>
          </div>
          <p className="mt-5 text-sm text-[#A1A1AA] max-w-sm leading-relaxed">
            Premier custom entertainment creations — luxury media walls, accent
            features and architectural installations across Florida&apos;s Gulf
            Coast.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            Explore
          </div>
          <ul className="space-y-2 text-sm">
            {[
              ["Services", "services"],
              ["Gallery", "gallery"],
              ["Process", "process"],
              ["About", "about"],
              ["Contact", "contact"],
            ].map(([label, id]) => (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => goToAnchor(id)}
                  data-testid={`footer-link-${id}`}
                  className="link-gold text-[#D1D1D6] bg-transparent text-left"
                >
                  {label}
                </button>
              </li>
            ))}
            <li>
              <Link
                to="/services"
                data-testid="footer-link-services-hub"
                className="link-gold text-[#D1D1D6]"
              >
                All Services
              </Link>
            </li>
            <li>
              <Link
                to="/locations"
                data-testid="footer-link-locations"
                className="link-gold text-[#D1D1D6]"
              >
                Service Areas
              </Link>
            </li>
            <li>
              <Link
                to="/design-my-wall"
                data-testid="footer-link-design"
                className="link-gold text-[#D1D1D6]"
              >
                Design My Wall
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            Contact
          </div>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                data-testid="footer-call"
                href={`tel:${SITE.phoneDigits}`}
                className="flex items-center gap-3 text-white link-gold"
              >
                <Phone className="h-4 w-4 text-[#D4AF37]" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a
                data-testid="footer-email"
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-3 text-white link-gold break-all"
              >
                <Mail className="h-4 w-4 text-[#D4AF37]" /> {SITE.email}
              </a>
            </li>
          </ul>
          <div className="flex items-center gap-3 mt-6">
            <a
              data-testid="footer-facebook"
              href="https://www.facebook.com/profile.php?id=61590875931181"
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 w-9 rounded-full border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] transition"
              aria-label="Facebook"
            >
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1a1a1a]">
        <div className="container-x px-6 md:px-12 lg:px-24 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#6b6b72]">
          <div>
            © {new Date().getFullYear()} Swift Luxury Wall Systems. All rights
            reserved.
          </div>
          <div className="tracking-[0.22em] uppercase">
            Premier Custom Entertainment Creations · Tampa Bay, FL
          </div>
        </div>
        <div className="container-x px-6 md:px-12 lg:px-24 pb-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-[11px] text-[#6b6b72]">
          <span>
            Website Created by{" "}
            <a
              data-testid="footer-credit"
              href="https://www.GregMiceli.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:underline"
            >
              www.GregMiceli.com
            </a>
          </span>
          <span className="hidden sm:inline text-[#3f3f46]">·</span>
          <Link
            to="/admin/login"
            data-testid="footer-owner-login"
            className="inline-flex items-center gap-1.5 text-[#6b6b72] hover:text-[#D4AF37] transition-colors"
          >
            <Lock className="h-3 w-3" /> Owner login
          </Link>
        </div>
      </div>
    </footer>
  );
}
